import { useEffect, useMemo, useState } from "react"
import { useThresholdValidation } from "./useThresholdValidation"
import AlertSetting from "../AlertSetting"
import EditableRow from "./EditableRow"
import Actions from "@/components/AlertSetting/Actions"
import Dialog from "@/components/shared/Dialog"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import Toggle from "@/components/shared/Toggle"
import Tooltip from "@/components/shared/Tooltip"
import { TestId } from "@/constants/globals"
import useDialog from "@/hooks/useDialog"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { AlertThreshold, ProcessingMethod } from "@/types/alert"
import "./index.scss"

export interface ThresholdGroup<T> {
   iconName: string
   label: string
   limitLabel: string
   key: keyof T
   durationKey: keyof T
   isLow?: boolean
}

export interface AlertThresholdSettingsProps<T> {
   disabled: boolean
   title: string
   iconName: IconProps["name"]
   extremeThreshold: number
   thresholdGroups: ThresholdGroup<T>[]
   form: T
   toggleable?: boolean
   dynamicToggleDisabled?: boolean
   onDynamicChangeSaved?: () => void
   onSave: (form: T) => void
}

const AlertThresholdSettings = <
   T extends Record<string, unknown> & Pick<AlertThreshold, "processingMethod">,
>({
   disabled,
   title,
   iconName,
   extremeThreshold,
   thresholdGroups,
   form,
   toggleable,
   dynamicToggleDisabled = false,
   onDynamicChangeSaved,
   onSave,
}: AlertThresholdSettingsProps<T>) => {
   const initialForm = useMemo(() => {
      return {
         ...form,
      }
   }, [form])

   const [isEditing, setIsEditing] = useState(false)
   const [formState, setFormState] = useFormStateHandler(form)
   const isDynamicToggleOn =
      formState.processingMethod === ProcessingMethod.DYNAMIC
   const { isOpen: isDialogOpen, openDialog, closeDialog } = useDialog()
   const { thresholdsValid, getThresholdTooltip } = useThresholdValidation(
      extremeThreshold,
      formState,
      thresholdGroups,
      isDynamicToggleOn
   )
   useEffect(() => {
      setFormState(form)
   }, [form, setFormState])

   useEffect(() => {
      if (disabled && isEditing) {
         setFormState(form)
         setIsEditing(false)
      }
   }, [disabled, isEditing, form, setFormState])

   const isFormChanged = useMemo(() => {
      const keys = Object.keys(initialForm) as (keyof typeof initialForm)[]
      return keys.some((key) => initialForm[key] !== formState[key])
   }, [initialForm, formState])

   const isDynamicChanged =
      initialForm.processingMethod !== formState.processingMethod

   const isDurationValid = useMemo(
      () =>
         isDynamicToggleOn ||
         thresholdGroups.every((group) => {
            if (!group.isLow) return true
            return (formState[group.durationKey] as number) >= 30
         }),
      [isDynamicToggleOn, thresholdGroups, formState]
   )

   const handleEdit = () => {
      setIsEditing(true)
   }

   const handleCancel = () => {
      setFormState(form)
      setIsEditing(false)
   }

   const commitSave = () => {
      const shouldShowDynamicSnackbar = isDynamicChanged && isDynamicToggleOn
      onSave(formState)
      setIsEditing(false)
      closeDialog()
      if (shouldShowDynamicSnackbar) {
         onDynamicChangeSaved?.()
      }
   }

   const handleSaveClick = () => {
      if (toggleable && isDynamicChanged && isDynamicToggleOn) {
         openDialog()
         return
      }
      commitSave()
   }

   return (
      <AlertSetting
         disabled={disabled}
         iconColor={IconColor.HONOLULU_BLUE}
         iconName={iconName}
         title={title}
      >
         <div className="alert-threshold-setting">
            {thresholdGroups.map((group, index) => (
               <div
                  key={String(group.key)}
                  className="alert-threshold-setting__threshold-group"
               >
                  <div className="alert-threshold-setting__title">
                     <div className="alert-threshold-setting__icon-title-wrapper">
                        <Icon
                           size="14"
                           name={group.iconName as IconProps["name"]}
                           color={IconColor.GUNMETAL}
                        />
                        <span>{group.label}</span>
                     </div>
                     {index === 0 && (
                        <Actions
                           isDisabled={
                              !thresholdsValid ||
                              !isDurationValid ||
                              !isFormChanged
                           }
                           isEditing={isEditing}
                           onCancel={handleCancel}
                           onEdit={handleEdit}
                           onSave={handleSaveClick}
                        />
                     )}
                  </div>
                  <table>
                     <tbody>
                        <EditableRow
                           label={
                              isDynamicToggleOn ? group.limitLabel : "Limit"
                           }
                           min={0}
                           valueKey={group.key}
                           isEditing={isEditing}
                           form={formState}
                           setFormState={setFormState}
                           thresholdKeyTooltip={getThresholdTooltip(
                              String(group.key)
                           )}
                        />
                        {!isDynamicToggleOn && (
                           <EditableRow
                              label="For X seconds"
                              min={group.isLow ? 30 : 0}
                              valueKey={group.durationKey}
                              isEditing={isEditing}
                              form={formState}
                              setFormState={setFormState}
                           />
                        )}
                     </tbody>
                  </table>
               </div>
            ))}
            {toggleable && (
               <div className="alert-threshold-setting__toggle">
                  <Toggle
                     label="Dynamic"
                     value={isDynamicToggleOn}
                     disabled={!isEditing || dynamicToggleDisabled}
                     onChange={() =>
                        setFormState({
                           ...formState,
                           processingMethod: isDynamicToggleOn
                              ? ProcessingMethod.STATIC
                              : ProcessingMethod.DYNAMIC,
                        })
                     }
                  />
                  <Tooltip
                     key="cancel-tooltip"
                     content={
                        <>
                           Alarm is triggered based on deviation from <br />
                           the current average, using the configured <br />
                           percentages. Thresholds adapt automatically <br />
                           instead of staying fixed.
                        </>
                     }
                  >
                     <Icon
                        name="info"
                        color={IconColor.HONOLULU_BLUE}
                        size="16"
                     />
                  </Tooltip>
               </div>
            )}
         </div>
         {isDialogOpen && (
            <Dialog
               open={isDialogOpen}
               title="Are you sure you want to switch the thresholds to dynamic mode? If confirmed the setting will become active 30 minutes after the first reading is received"
               titleTestId={TestId.DYNAMIC_THRESHOLD_DIALOG_TITLE}
               buttonTitle="Confirm"
               handleClose={closeDialog}
               handleButtonClick={commitSave}
            />
         )}
      </AlertSetting>
   )
}

export default AlertThresholdSettings
